import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { IconButton, Paper, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import FileIcon from "@entrilia/shared/components/FileIcon";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { formatFileSize, getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import adminApi from "../../../../../api/adminApi";
import type { ChangeRequestAttachment } from "../../../../../api/types/workflowTypes";

interface Props {
  workflowId: string;
  attachment: ChangeRequestAttachment;
}

// Request-scoped: reviewers hold ViewInvestorRequests, not the ViewAllCompanyFiles the generic download endpoint requires
const getDownloadInfo = withErrorHandling(adminApi.getMemberContributionAttachmentDownloadInfo);

const ContributionAttachmentRow = ({ workflowId, attachment }: Props) => {
  const [downloading, setDownloading] = useState(false);
  const { sendNotificationError } = useNotificationContext();

  const handleDownload = async () => {
    setDownloading(true);
    const [downloadInfo, error] = await getDownloadInfo(workflowId, attachment.dataCatalogId);
    setDownloading(false);
    if (error) {
      logError(error, "[ContributionAttachmentRow] getMemberContributionAttachmentDownloadInfo");
      sendNotificationError(error.message);
      return;
    }
    await downloadFileFromUrl(downloadInfo.downloadUrl);
  };

  return (
    <Paper variant="outlined">
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", px: 1.5, py: 1 }}>
        <FileIcon fileExtension={getFileExtension(attachment.name)} />
        <Typography noWrap sx={{ flex: 1 }}>
          {attachment.name}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {formatFileSize(attachment.size)}
        </Typography>
        <IconButton loading={downloading} onClick={handleDownload}>
          <DownloadIcon />
        </IconButton>
      </Stack>
    </Paper>
  );
};

export default ContributionAttachmentRow;
